import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Shield, Sparkles, Crown } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CustomerOnboardDialog = ({ open, onOpenChange, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingSiteId, setCheckingSiteId] = useState(false);
  const [siteIdAvailable, setSiteIdAvailable] = useState(null);
  const [form, setForm] = useState({
    site_id: '',
    company_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    gst_number: '',
    pan_number: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_designation: '',
    edition: 'standard',
    creator_licenses: 5,
    viewer_licenses: 20,
    duration_months: 12,
    notes: '',
  });

  const validateSiteId = async (value) => {
    if (value.length < 10) {
      setSiteIdAvailable({ valid: false, message: 'Minimum 10 characters' });
      return;
    }
    if (value.length > 20) {
      setSiteIdAvailable({ valid: false, message: 'Maximum 20 characters' });
      return;
    }
    if (/\s/.test(value)) {
      setSiteIdAvailable({ valid: false, message: 'No spaces allowed' });
      return;
    }

    setCheckingSiteId(true);
    try {
      const response = await axios.get(`${API_URL}/oem/validate-site-id/${value}`);
      setSiteIdAvailable({ valid: response.data.available, message: response.data.available ? 'Available' : response.data.reason });
    } catch (error) {
      setSiteIdAvailable({ valid: false, message: 'Validation failed' });
    } finally {
      setCheckingSiteId(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        site_id: form.site_id,
        company_name: form.company_name,
        address: {
          address_line1: form.address_line1,
          address_line2: form.address_line2 || null,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
          country: form.country,
        },
        gst_number: form.gst_number || null,
        pan_number: form.pan_number || null,
        contact_person: {
          name: form.contact_name,
          email: form.contact_email,
          phone: form.contact_phone,
          designation: form.contact_designation || null,
        },
        edition: form.edition,
        license: {
          creator_licenses: parseInt(form.creator_licenses),
          viewer_licenses: parseInt(form.viewer_licenses),
          duration_months: parseInt(form.duration_months),
        },
        notes: form.notes || null,
      };

      const response = await axios.post(`${API_URL}/oem/tenants`, payload);
      toast.success('Customer onboarded successfully!');
      
      if (response.data.license_key) {
        toast.info(`License Key: ${response.data.license_key}`, { duration: 8000 });
      }
      
      onSuccess();
      setStep(1);
      setForm({
        site_id: '', company_name: '', address_line1: '', address_line2: '', city: '', state: '',
        pincode: '', country: 'India', gst_number: '', pan_number: '', contact_name: '', contact_email: '',
        contact_phone: '', contact_designation: '', edition: 'standard', creator_licenses: 5,
        viewer_licenses: 20, duration_months: 12, notes: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to onboard customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Onboard New Customer</DialogTitle>
          <DialogDescription>Step {step} of 3</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Company Information</h3>
            
            <div className="space-y-2">
              <Label>Site ID *</Label>
              <div className="relative">
                <Input
                  value={form.site_id}
                  onChange={(e) => {
                    const value = e.target.value.toLowerCase().replace(/\s/g, '');
                    setForm(prev => ({ ...prev, site_id: value }));
                    if (value.length >= 10) validateSiteId(value);
                    else setSiteIdAvailable(null);
                  }}
                  placeholder="companyname01"
                  className={siteIdAvailable?.valid === false ? 'border-red-500' : siteIdAvailable?.valid ? 'border-green-500' : ''}
                />
                {checkingSiteId && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />}
              </div>
              {siteIdAvailable && (
                <p className={`text-xs ${siteIdAvailable.valid ? 'text-green-500' : 'text-red-500'}`}>
                  {siteIdAvailable.message}
                </p>
              )}
              <p className="text-xs text-muted-foreground">10-20 characters, no spaces. Used for tenant identification.</p>
            </div>

            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input value={form.company_name} onChange={(e) => setForm(prev => ({ ...prev, company_name: e.target.value }))} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>GST Number</Label>
                <Input value={form.gst_number} onChange={(e) => setForm(prev => ({ ...prev, gst_number: e.target.value.toUpperCase() }))} maxLength={15} />
              </div>
              <div className="space-y-2">
                <Label>PAN Number</Label>
                <Input value={form.pan_number} onChange={(e) => setForm(prev => ({ ...prev, pan_number: e.target.value.toUpperCase() }))} maxLength={10} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Address Line 1 *</Label>
              <Input value={form.address_line1} onChange={(e) => setForm(prev => ({ ...prev, address_line1: e.target.value }))} />
            </div>

            <div className="space-y-2">
              <Label>Address Line 2</Label>
              <Input value={form.address_line2} onChange={(e) => setForm(prev => ({ ...prev, address_line2: e.target.value }))} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City *</Label>
                <Input value={form.city} onChange={(e) => setForm(prev => ({ ...prev, city: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>State *</Label>
                <Input value={form.state} onChange={(e) => setForm(prev => ({ ...prev, state: e.target.value }))} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pin Code *</Label>
                <Input value={form.pincode} onChange={(e) => setForm(prev => ({ ...prev, pincode: e.target.value }))} maxLength={6} />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input value={form.country} onChange={(e) => setForm(prev => ({ ...prev, country: e.target.value }))} />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Contact Person</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={form.contact_name} onChange={(e) => setForm(prev => ({ ...prev, contact_name: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Designation</Label>
                <Input value={form.contact_designation} onChange={(e) => setForm(prev => ({ ...prev, contact_designation: e.target.value }))} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={form.contact_email} onChange={(e) => setForm(prev => ({ ...prev, contact_email: e.target.value }))} />
            </div>

            <div className="space-y-2">
              <Label>Phone *</Label>
              <Input value={form.contact_phone} onChange={(e) => setForm(prev => ({ ...prev, contact_phone: e.target.value }))} />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-semibold">License Details</h3>

            <div className="space-y-2">
              <Label>Product Edition *</Label>
              <Select value={form.edition} onValueChange={(v) => setForm(prev => ({ ...prev, edition: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Standard - Basic LMS features
                    </div>
                  </SelectItem>
                  <SelectItem value="professional">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Professional - HR/WhatsApp + AI Recommendations
                    </div>
                  </SelectItem>
                  <SelectItem value="enterprise">
                    <div className="flex items-center gap-2">
                      <Crown className="w-4 h-4" />
                      Enterprise - All Features
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Creator Licenses *</Label>
                <Input type="number" min="1" value={form.creator_licenses} onChange={(e) => setForm(prev => ({ ...prev, creator_licenses: e.target.value }))} />
                <p className="text-xs text-muted-foreground">Admins & content creators</p>
              </div>
              <div className="space-y-2">
                <Label>Viewer Licenses *</Label>
                <Input type="number" min="0" value={form.viewer_licenses} onChange={(e) => setForm(prev => ({ ...prev, viewer_licenses: e.target.value }))} />
                <p className="text-xs text-muted-foreground">Learners with view-only access</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>License Duration *</Label>
              <Select value={form.duration_months.toString()} onValueChange={(v) => setForm(prev => ({ ...prev, duration_months: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Month</SelectItem>
                  <SelectItem value="3">3 Months</SelectItem>
                  <SelectItem value="6">6 Months</SelectItem>
                  <SelectItem value="12">12 Months (1 Year)</SelectItem>
                  <SelectItem value="24">24 Months (2 Years)</SelectItem>
                  <SelectItem value="36">36 Months (3 Years)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Internal notes about this customer..." />
            </div>

            <Card className="bg-muted/50">
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">
                  {form.edition === 'standard' && 'Standard Edition Features'}
                  {form.edition === 'professional' && 'Professional Edition Features'}
                  {form.edition === 'enterprise' && 'Enterprise Edition Features'}
                </h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>✓ Core LMS features</li>
                  <li>✓ Custom branding</li>
                  {form.edition !== 'standard' && <li>✓ HR Integration</li>}
                  {form.edition !== 'standard' && <li>✓ WhatsApp Integration</li>}
                  {form.edition !== 'standard' && <li>✓ AI Course Recommendations</li>}
                  {form.edition === 'enterprise' && <li>✓ AI Course Generation</li>}
                  {form.edition === 'enterprise' && <li>✓ Content Moderation</li>}
                  {form.edition === 'enterprise' && <li>✓ Advanced Analytics</li>}
                </ul>
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter>
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(step - 1)}>
              Back
            </Button>
          )}
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)} disabled={
              (step === 1 && (!form.site_id || !form.company_name || !form.city || !form.state || !form.pincode || !siteIdAvailable?.valid)) ||
              (step === 2 && (!form.contact_name || !form.contact_email || !form.contact_phone))
            }>
              Next
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create Customer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
